/**
 * Notifications Store
 * Manages in-app notifications state, fetching, and read status
 */

import { create } from 'zustand';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, orderBy, limit, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { functions, db } from '../lib/firebase';
import { getErrorMessage } from '../lib/api';
import type { NotificationDocument } from '../lib/types';

interface NotificationsState {
  notifications: NotificationDocument[];
  unreadCount: number;
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  initialized: boolean;

  // Actions
  fetchNotifications: (loadMore?: boolean) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  subscribeToNotifications: (uid: string) => () => void;
  reset: () => void;
}

// Track the notification listener for cleanup
let notificationUnsubscribe: Unsubscribe | null = null;

// API response types
interface GetNotificationsResponse {
  notifications: NotificationDocument[];
  hasMore: boolean;
  unreadCount: number;
}

interface MarkNotificationReadResponse {
  success: boolean;
}

interface MarkAllNotificationsReadResponse {
  success: boolean;
  count: number;
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  hasMore: false,
  error: null,
  initialized: false,

  fetchNotifications: async (loadMore = false) => {
    const state = get();
    if (state.loading) return;

    set({ loading: true, error: null });

    try {
      const getNotifications = httpsCallable<
        { limit?: number; beforeTimestamp?: string },
        GetNotificationsResponse
      >(functions, 'getNotifications');

      // For pagination, get the last notification's timestamp
      const beforeTimestamp = loadMore && state.notifications.length > 0
        ? state.notifications[state.notifications.length - 1].createdAt
        : undefined;

      const result = await getNotifications({
        limit: 20,
        beforeTimestamp,
      });

      const newNotifications = result.data.notifications;

      set({
        notifications: loadMore
          ? [...state.notifications, ...newNotifications]
          : newNotifications,
        hasMore: result.data.hasMore,
        unreadCount: result.data.unreadCount,
        loading: false,
        initialized: true,
      });
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false });
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const markNotificationRead = httpsCallable<
        { notificationId: string },
        MarkNotificationReadResponse
      >(functions, 'markNotificationRead');

      await markNotificationRead({ notificationId });

      // Optimistically update the local state
      const state = get();
      set({
        notifications: state.notifications.map((n) =>
          n.id === notificationId ? { ...n, read: true, readAt: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      });
    } catch (err) {
      set({ error: getErrorMessage(err) });
    }
  },

  markAllAsRead: async () => {
    try {
      const markAllNotificationsRead = httpsCallable<
        Record<string, never>,
        MarkAllNotificationsReadResponse
      >(functions, 'markAllNotificationsRead');

      await markAllNotificationsRead({});

      // Update all notifications in state
      const state = get();
      set({
        notifications: state.notifications.map((n) => ({
          ...n,
          read: true,
          readAt: n.readAt || new Date().toISOString(),
        })),
        unreadCount: 0,
      });
    } catch (err) {
      set({ error: getErrorMessage(err) });
    }
  },

  subscribeToNotifications: (uid: string) => {
    // Clean up existing subscription
    if (notificationUnsubscribe) {
      notificationUnsubscribe();
    }

    // Subscribe to unread count changes (lightweight)
    const notificationsRef = collection(db, 'users', uid, 'notifications');
    const unreadQuery = query(
      notificationsRef,
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(50) // Cap for performance
    );

    notificationUnsubscribe = onSnapshot(
      unreadQuery,
      (snapshot) => {
        set({ unreadCount: snapshot.size });

        // If we get a new notification, refresh the list
        const state = get();
        if (state.initialized && snapshot.docChanges().some((c) => c.type === 'added')) {
          get().fetchNotifications();
        }
      },
      (error) => {
        console.error('[Notifications] Snapshot error:', error);
      }
    );

    // Initial fetch
    get().fetchNotifications();

    return () => {
      if (notificationUnsubscribe) {
        notificationUnsubscribe();
        notificationUnsubscribe = null;
      }
    };
  },

  reset: () => {
    if (notificationUnsubscribe) {
      notificationUnsubscribe();
      notificationUnsubscribe = null;
    }
    set({
      notifications: [],
      unreadCount: 0,
      loading: false,
      hasMore: false,
      error: null,
      initialized: false,
    });
  },
}));

